/**
 * fnoBars — maps the /options/fno-series samples onto the chart's candles.
 *
 * Each bar shows the FNO state AS OF ITS CLOSE: the last sample stamped before the next
 * bar opens (the forming bar: the newest sample). That is the projectToBarClose rule, so
 * a closed bar never repaints and the right edge is the same on every timeframe.
 *
 * On top of that, a bar only counts as having data when its sample is recent: inside the
 * bar, or at most `tolSec` (20 min = four missed polls) before it opened. Older than that
 * the bar is EMPTY, so a collector outage draws as a break, never as a flat line holding
 * a stale value.
 *
 * Times: chart time = IST-shifted epoch seconds (candle.time); samples carry UTC epoch ms.
 */

export type FnoLvl = [number, number | null];   // [strike, OI]
export interface FnoSample {
  t: number;          // UTC epoch ms
  x: string;          // expiry 'YYYY-MM-DD'
  pcr: number | null; // put/call OI ratio
  iv: number | null;  // ATM IV, fraction
  mp: number | null;  // max pain strike
  gf: number | null;  // zero-gamma flip price
  ng: number | null;  // net gamma (sign = regime)
  sp: number | null;  // spot
  st: number | null;  // ATM straddle premium (≈ the market's expected move to expiry)
  ce: number | null;  // total call OI (strike band)
  pe: number | null;  // total put OI
  sup: FnoLvl[];      // put-OI walls ranked by OI (index 0 = the put wall)
  res: FnoLvl[];      // call-OI walls ranked by OI (index 0 = the call wall)
  wp?: Array<[number, number]>;  // +γ walls
  wn?: Array<[number, number]>;  // −γ walls
}
export interface FnoExpiry { date: string; kind: 'D' | 'W' | 'M'; settleMs: number; live: boolean; firstMs: number | null; lastMs: number | null; count: number }
export interface FnoSeriesResp {
  supported: boolean;
  reason?: string;
  symbol?: string;
  sel?: string;
  pinned?: boolean;
  bucketSec?: number;
  tolMs?: number;
  samples: FnoSample[];
  rolls: Array<{ t: number; from: string; to: string }>;
  gaps: Array<[number, number]>;
  expiries: FnoExpiry[];
}

export interface FnoBar {
  time: number;             // chart time (bar open)
  s: FnoSample | null;      // the state at the bar's close; null = no data for this bar
  dce: number | null;       // call OI change vs the previous bar (same expiry only)
  dpe: number | null;       // put OI change
  roll: string | null;      // expiry this bar rolled INTO (rolling mode), else null
}

const IST = 19800;
export const sampleChartTime = (s: { t: number }) => Math.floor(s.t / 1000) + IST;

export function projectFnoBars(
  samples: FnoSample[],
  candleTimes: number[],
  opts?: { tolSec?: number; maxTime?: number | null },
): FnoBar[] {
  const tol = opts?.tolSec ?? 20 * 60;
  const maxTime = opts?.maxTime ?? null;
  const n = candleTimes.length;
  if (!n) return [];
  const s = samples.slice().sort((a, b) => a.t - b.t);
  const st = s.map(sampleChartTime);
  let span = Infinity;
  if (n >= 2) {
    const d: number[] = [];
    for (let i = 1; i < Math.min(n, 50); i++) d.push(candleTimes[i] - candleTimes[i - 1]);
    d.sort((a, b) => a - b);
    span = d[Math.floor(d.length / 2)] || Infinity;
  }
  const out: FnoBar[] = [];
  let j = -1;
  for (let i = 0; i < n; i++) {
    const ct = candleTimes[i];
    if (maxTime != null && ct > maxTime) break;
    const end = i + 1 < n ? candleTimes[i + 1] : (maxTime != null && Number.isFinite(span) ? ct + span : Infinity);
    while (j + 1 < s.length && st[j + 1] < end) j++;
    const smp = j >= 0 && st[j] >= ct - tol ? s[j] : null;
    const prev = out.length ? out[out.length - 1] : null;
    const sameExp = !!(smp && prev?.s && prev.s.x === smp.x);
    out.push({
      time: ct,
      s: smp,
      dce: sameExp && smp!.ce != null && prev!.s!.ce != null ? smp!.ce - prev!.s!.ce : null,
      dpe: sameExp && smp!.pe != null && prev!.s!.pe != null ? smp!.pe - prev!.s!.pe : null,
      roll: smp && prev?.s && prev.s.x !== smp.x ? smp.x : null,
    });
  }
  return out;
}

/** Per-bar values for a line: null where the bar has no data or the value is missing. */
export const barValues = (bars: FnoBar[], pick: (s: FnoSample) => number | null | undefined) =>
  bars.map(b => ({ time: b.time, value: b.s ? (pick(b.s) ?? null) : null }));

/** Bars inside a collector outage (`gaps` are [from,to] UTC ms). */
export function gapBarTimes(bars: FnoBar[], gaps: Array<[number, number]>): Set<number> {
  const out = new Set<number>();
  if (!gaps?.length) return out;
  const g = gaps.map(([a, b]) => [Math.floor(a / 1000) + IST, Math.floor(b / 1000) + IST] as [number, number]);
  for (const b of bars) {
    if (b.s) continue;
    if (g.some(([a, z]) => b.time >= a - 1800 && b.time <= z)) out.add(b.time);
  }
  return out;
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** '2026-11-04' → '04 Nov' */
export const fmtExpiry = (iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : `${String(d.getUTCDate()).padStart(2, '0')} ${MON[d.getUTCMonth()]}`;
};
/** Time left to settlement, compact: '13h', '2d', '25d'. */
export const fmtLeft = (settleMs: number, now = Date.now()) => {
  const h = (settleMs - now) / 3600e3;
  if (h <= 0) return 'settled';
  return h < 24 ? `${Math.max(1, Math.round(h))}h` : `${Math.round(h / 24)}d`;
};
